import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";

const RANGES = [
  { label: "1W", days: 7 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
];

export default function PortfolioTrendChart() {
  const { authHeaders } = useAuth();
  const [points, setPoints] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const headers = authHeaders();
    if (!headers.Authorization) { setPoints([]); return; }
    axios
      .get(`${API}/portfolio/history`, { headers, params: { days } })
      .then(({ data }) => setPoints(data.points || []))
      .catch((err) => { console.debug("Portfolio history unavailable:", err); setPoints([]); });
  }, [authHeaders, days]);

  if (!points) return null;

  const rising = points.length < 2 || points[points.length - 1].total_value >= points[0].total_value;
  const color = rising ? "#00FFA3" : "#FF3B30";

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="portfolio-trend-section">
      <Card className="glass-effect border border-white/10 mb-6">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#00E0FF]" />
              <h2 className="text-sm font-bold text-white uppercase tracking-wider">Portfolio Trend</h2>
            </div>
            <div className="flex items-center gap-1" data-testid="trend-range-picker">
              {RANGES.map((r) => (
                <button
                  key={r.label}
                  data-testid={`trend-range-${r.label.toLowerCase()}`}
                  onClick={() => setDays(r.days)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                    days === r.days
                      ? "bg-[#00E0FF]/15 text-[#00E0FF] border border-[#00E0FF]/30"
                      : "text-gray-500 hover:text-white border border-transparent"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {points.length === 0 ? (
            <p className="text-xs text-gray-500" data-testid="portfolio-trend-empty">
              No snapshots in this range yet — your daily value history builds automatically.
            </p>
          ) : points.length < 2 ? (
            <p className="text-xs text-gray-500" data-testid="portfolio-trend-empty">
              First snapshot saved at ${points[0].total_value.toLocaleString()} — check back tomorrow to see your trend line grow.
            </p>
          ) : (
            <div className="h-44" data-testid="portfolio-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={32} />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} tickLine={false} axisLine={false} width={64}
                    domain={["auto", "auto"]} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                  <Tooltip
                    contentStyle={{ background: "#0b1020", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "#9CA3AF" }}
                    formatter={(v) => [`$${Number(v).toLocaleString()}`, "Value"]}
                  />
                  <Area type="monotone" dataKey="total_value" stroke={color} strokeWidth={2} fill="url(#trendFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
